import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faXmark, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import type { ReportReadiness } from '@/services/inspection-report.service';
import {
  readinessItems,
  PROCEDURE_REQUIREMENT_COPY,
  PROCEDURE_VS_INTAKE_HELP,
  type ProcedureRequirementState,
} from '../../report/report-workflow';

interface Props {
  readiness: ReportReadiness;
  /**
   * Why the procedure requirement is in the state it is. Optional: without it
   * the row renders as a plain met/unmet line, which is what the readiness
   * payload alone can express. The parent supplies it because only the parent
   * knows whether a procedure execution exists.
   */
  procedureState?: ProcedureRequirementState;
}

/** Shows the six minimum requirements; clearly marks what is still missing so
 * the engineer knows why Generate is disabled. */
export function ReportReadinessBanner({ readiness, procedureState }: Props) {
  const items = readinessItems(readiness);
  const missing = items.filter((i) => !i.met).length;

  return (
    <div
      className={`rounded-lg border px-4 py-3 ${
        readiness.canGenerate
          ? 'border-green-300 bg-green-50 dark:border-green-900/50 dark:bg-green-900/20'
          : 'border-amber-300 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/20'
      }`}
      data-testid="report-readiness"
    >
      <p className="mb-2 text-sm font-semibold text-foreground">
        {readiness.canGenerate
          ? 'All requirements met — the report can be generated.'
          : `${missing} requirement${missing !== 1 ? 's' : ''} still missing before the report can be generated.`}
      </p>
      <ul className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
        {items.map((item) => {
          // The procedure requirement explains ITSELF: which of the two unmet
          // causes applies, what to do about it, and that it is not the intake
          // condition checklist. Every other requirement is self-evident from
          // its label alone.
          const proc = item.key === 'hasCompletedChecklist' ? procedureState : undefined;
          const copy = proc ? PROCEDURE_REQUIREMENT_COPY[proc] : null;
          const icon = copy
            ? copy.tone === 'success'
              ? faCheck
              : copy.tone === 'warning'
                ? faTriangleExclamation
                : faXmark
            : item.met
              ? faCheck
              : faXmark;
          const iconClass = copy
            ? copy.tone === 'success'
              ? 'text-green-600'
              : copy.tone === 'warning'
                ? 'text-amber-500'
                : 'text-red-500'
            : item.met
              ? 'text-green-600'
              : 'text-red-500';

          return (
            <li
              key={item.key}
              className="flex items-start gap-2 text-sm"
              data-testid={`readiness-${item.key}`}
            >
              <FontAwesomeIcon icon={icon} className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${iconClass}`} />
              <div className="min-w-0">
                <span className={item.met ? 'text-foreground' : 'text-muted-foreground'}>
                  {copy ? copy.label : item.label}
                </span>
                {copy && !item.met && (
                  <>
                    <p className="mt-0.5 text-xs text-muted-foreground">{copy.help}</p>
                    <p className="mt-0.5 text-xs italic text-muted-foreground">
                      {PROCEDURE_VS_INTAKE_HELP}
                    </p>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
